import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setLogin, setLogout } from "state";
import Layout from "components/Layout";
import ConfirmDialog from "components/ConfirmDialog";
import api from "utils/api";
import { formatPrice } from "constants/massages";
import ProfileSettings, { PasswordSettings } from "./ProfileSettings";
import { APP_BRAND } from "utils/brand";
import "styles/konto.css";

const formatLong = (date) =>
  new Date(date).toLocaleString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const KontoPage = () => {
  const user = useSelector((s) => s.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleLogout = () => {
    dispatch(setLogout());
    navigate("/");
  };

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelId, setCancelId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteMsg, setDeleteMsg] = useState(null);
  const [referral, setReferral] = useState(null);
  const [referralCopied, setReferralCopied] = useState(false);
  const token = useSelector((s) => s.token);

  const loadReferral = async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data?.user) {
        dispatch(setLogin({ token, user: data.user }));
        setReferral({
          link: data.user.referralLink,
          balanceCents: data.user.referralBalanceCents || 0,
          useCount: data.user.referralUseCount || 0,
        });
      }
    } catch {
      /* optional panel */
    }
  };

  const copyReferralLink = async () => {
    if (!referral?.link) return;
    try {
      await navigator.clipboard.writeText(referral.link);
      setReferralCopied(true);
      window.setTimeout(() => setReferralCopied(false), 2000);
    } catch {
      setReferralCopied(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings/mine");
      setBookings(data.bookings || []);
    } catch (err) {
      setError(err?.response?.data?.message || "Buchungen konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadReferral();
  }, []);

  useEffect(() => {
    const cancelledId = searchParams.get("paymentCancelled");
    if (!cancelledId) return undefined;
    let active = true;
    (async () => {
      try {
        await api.delete(`/bookings/${cancelledId}`);
        if (active) {
          setSearchParams({}, { replace: true });
          await load();
        }
      } catch {
        if (active) setSearchParams({}, { replace: true });
      }
    })();
    return () => {
      active = false;
    };
  }, [searchParams, setSearchParams]);

  const confirmDeleteAccount = async () => {
    setDeleting(true);
    setDeleteMsg(null);
    try {
      const { data } = await api.post("/auth/request-account-deletion", {
        brand: APP_BRAND,
      });
      setDeleteOpen(false);
      setDeleteMsg({
        kind: "success",
        text:
          data.message ||
          "Eine Bestätigungs-E-Mail wurde an deine Adresse gesendet.",
      });
    } catch (err) {
      setDeleteMsg({
        kind: "error",
        text: err?.response?.data?.message || "Anfrage fehlgeschlagen.",
      });
    } finally {
      setDeleting(false);
    }
  };

  const confirmCancel = async () => {
    if (!cancelId) return;
    setCancelling(true);
    try {
      await api.delete(`/bookings/${cancelId}`);
      setCancelId(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Stornierung fehlgeschlagen.");
    } finally {
      setCancelling(false);
    }
  };

  const cancelledBookingHint = (booking) => {
    if (booking.status !== "cancelled") return null;
    if (booking.cancelledBy === "customer") return "Selbst storniert";
    if (booking.cancelledBy === "admin" || booking.cancelledBy === "staff") {
      return "Vom Team storniert";
    }
    if (booking.staffCancellationReason?.trim()) return "Vom Team storniert";
    return null;
  };

  const statusLabelKonto = (booking) => {
    const status = typeof booking === "string" ? booking : booking.status;
    if (status === "pending-employee-confirmation") return "Wartet auf Bestätigung";
    if (status === "confirmed") return "Bestätigt";
    if (status === "cancelled") {
      const hint =
        typeof booking === "object" ? cancelledBookingHint(booking) : null;
      return hint ? `Storniert (${hint})` : "Storniert";
    }
    return status;
  };

  const paymentLabelKonto = (b) => {
    const paid = b.paymentStatus === "paid" || b.paymentStatus === "not-applicable";
    const paidText = paid ? "Bezahlt" : "Unbezahlt";
    let channel = "Vor Ort";
    if (b.paymentMethod === "stripe") channel = "Online";
    else if (b.paymentMethod === "later") channel = "Nach Termin";
    return `${paidText} (${channel})`;
  };

  const upcoming = bookings.filter((b) => new Date(b.start) >= new Date() && b.status !== "cancelled");
  const past = bookings.filter((b) => new Date(b.start) < new Date() || b.status === "cancelled");
  const referralCard = referral?.link ? (
    <section className="konto-referral-card" aria-labelledby="konto-referral-title">
      <div className="konto-referral-head">
        <h2 id="konto-referral-title">Freunde empfehlen</h2>
        <p>
          Teile deinen persönlichen Link. Wenn sich jemand darüber registriert und
          erstmals eine Massage bezahlt, erhältst du einmalig{" "}
          <strong>5&nbsp;€ Guthaben</strong> für deine nächste Buchung.
        </p>
      </div>
      <div className="konto-referral-stats">
        <div>
          <span className="konto-referral-label">Guthaben</span>
          <strong>{formatPrice(referral.balanceCents)}</strong>
          <small>Nur für Massagen auf dieser Website</small>
        </div>
        <div>
          <span className="konto-referral-label">Link genutzt</span>
          <strong>{referral.useCount}×</strong>
          <small>Registrierungen über deinen Link</small>
        </div>
      </div>
      <div className="konto-referral-link-row">
        <input
          type="text"
          readOnly
          value={referral.link}
          aria-label="Dein Empfehlungslink"
        />
        <button type="button" className="btn-primary" onClick={copyReferralLink}>
          {referralCopied ? "Kopiert" : "Link kopieren"}
        </button>
      </div>
    </section>
  ) : null;

  return (
    <Layout>
      <section className="konto-page">
        <div className="konto-shell">
          <header className="konto-header">
            <div>
              <h1>
                Mein <em>Konto</em>
              </h1>
              <p>
                Willkommen{user?.firstName ? `, ${user.firstName}` : ""}. Hier siehst du
                kommende und vergangene Termine, kannst Buchungen ändern oder neue anlegen.
              </p>
            </div>
            <Link to="/buchen" className="btn-primary">
              Neuen Termin buchen
            </Link>
          </header>

          {error && <div className="auth-alert is-error">{error}</div>}

          <PasswordSettings user={user} />

          <h2 style={{ fontFamily: "var(--font-display)", color: "var(--ink)", marginBottom: "1rem" }}>
            Kommende Termine
          </h2>
          {loading ? (
            <div className="konto-empty">Termine werden geladen…</div>
          ) : upcoming.length === 0 ? (
            <div className="konto-empty">
              <h3>Keine kommenden Termine</h3>
              <p>
                Du hast aktuell keine offenen Buchungen.{" "}
                <Link to="/buchen" style={{ color: "var(--dark-blue)", fontWeight: 600 }}>
                  Jetzt buchen →
                </Link>
              </p>
            </div>
          ) : (
            <div className="booking-list">
              {upcoming.map((b) => (
                <article
                  key={b._id}
                  className={`booking-row ${b.status === "cancelled" ? "is-cancelled" : ""}`}
                >
                  <div>
                    <div className="booking-service">{b.serviceLabel}</div>
                    <div className="booking-meta">
                      <span><strong>Termin:</strong> {formatLong(b.start)}</span>
                      <span><strong>Dauer:</strong> {b.durationMinutes} Min.</span>
                      <span>
                        <strong>Preis:</strong>{" "}
                        {b.priceCents === 0 ? "Kostenfrei" : formatPrice(b.priceCents)}
                      </span>
                      <span>
                        <strong>Status:</strong> {statusLabelKonto(b)}
                      </span>
                      <span>
                        <strong>Zahlung:</strong> {paymentLabelKonto(b)}
                      </span>
                      <span><strong>Ort:</strong> {b.location}</span>
                    </div>
                  </div>
                  <div className="booking-actions">
                    <Link to={`/konto/buchungen/${b._id}/aendern`}>Termin ändern</Link>
                    <button type="button" className="danger" onClick={() => setCancelId(b._id)}>
                      Stornieren
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}

          <ProfileSettings brand={APP_BRAND} afterEmail={referralCard} />

          {past.length > 0 && (
            <>
              <h2
                style={{
                  fontFamily: "var(--font-display)",
                  color: "var(--ink)",
                  margin: "2.5rem 0 1rem",
                }}
              >
                Vergangene Termine
              </h2>
              <div className="booking-list">
                {past.map((b) => (
                  <article
                    key={b._id}
                    className={`booking-row ${b.status === "cancelled" ? "is-cancelled" : ""}`}
                  >
                    <div>
                      <div className="booking-service">{b.serviceLabel}</div>
                      <div className="booking-meta">
                        <span><strong>Termin:</strong> {formatLong(b.start)}</span>
                        {b.status === "cancelled" && (
                          <span>
                            <strong>Status:</strong> {statusLabelKonto(b)}
                          </span>
                        )}
                        <span>
                          <strong>Preis:</strong>{" "}
                          {b.priceCents === 0 ? "Kostenfrei" : formatPrice(b.priceCents)}
                        </span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}

          {deleteMsg && (
            <div className={`auth-alert is-${deleteMsg.kind}`}>{deleteMsg.text}</div>
          )}

          <div className="konto-footer">
            <button
              type="button"
              className="btn-ghost konto-logout"
              onClick={() => setLogoutOpen(true)}
            >
              Abmelden
            </button>
            <button
              type="button"
              className="konto-delete"
              onClick={() => setDeleteOpen(true)}
            >
              Konto löschen
            </button>
          </div>
        </div>

        <ConfirmDialog
          open={logoutOpen}
          title="Abmelden?"
          message="Möchtest du dich wirklich abmelden?"
          confirmLabel="Abmelden"
          cancelLabel="Abbrechen"
          danger={false}
          onConfirm={() => {
            setLogoutOpen(false);
            handleLogout();
          }}
          onCancel={() => setLogoutOpen(false)}
        />
        <ConfirmDialog
          open={Boolean(cancelId)}
          title="Buchung stornieren?"
          message="Möchtest du diese Buchung wirklich stornieren? Diese Aktion kann nicht rückgängig gemacht werden."
          confirmLabel="Stornieren"
          cancelLabel="Abbrechen"
          busy={cancelling}
          busyLabel="Wird storniert…"
          onConfirm={confirmCancel}
          onCancel={() => !cancelling && setCancelId(null)}
        />
        <ConfirmDialog
          open={deleteOpen}
          title="Konto wirklich löschen?"
          message="Möchtest du dein Konto wirklich löschen? Du erhältst eine E-Mail mit einem Bestätigungslink."
          confirmLabel="Ja, löschen"
          cancelLabel="Abbrechen"
          busy={deleting}
          busyLabel="Wird gesendet…"
          onConfirm={confirmDeleteAccount}
          onCancel={() => !deleting && setDeleteOpen(false)}
        />
      </section>
    </Layout>
  );
};

export default KontoPage;
